export function initContinuationDots() {
  const anchor = document.querySelector(".continuation-anchor");
  const spacer = document.querySelector(".continuation-spacer");
  const dots = document.querySelector(".continuation-dots");

  if (!anchor || !dots) {
    return;
  }

  const dotElements = [...dots.children];
  const periodMarkers = [...anchor.querySelectorAll(".continuation-period")];
  const fireflyDot = dots.querySelector(".firefly-dot");
  const fireflyShape = dots.querySelector(".firefly-shape");
  const fireflySvg = dots.querySelector(".firefly-dot svg");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  dots.setAttribute("aria-hidden", "true");
  document.body.append(dots);
  const teardropShape = {
    start: [20, -5],
    segments: [
      { control1: [26, -5], control2: [31.75, -2.62], end: [36, 1.63] },
      { control1: [40.24, 5.87], control2: [42.62, 11.62], end: [42.62, 17.62] },
      { control1: [42.62, 23.62], control2: [40.24, 29.37], end: [36, 33.62] },
      { control1: [30.9, 38.72], control2: [24.4, 47.02], end: [20, 47.02] },
      { control1: [15.6, 47.02], control2: [9.1, 38.72], end: [4, 33.62] },
      { control1: [-0.24, 29.37], control2: [-2.62, 23.62], end: [-2.62, 17.62] },
      { control1: [-2.62, 11.62], control2: [-0.24, 5.87], end: [4, 1.63] },
      { control1: [8.25, -2.62], control2: [14, -5], end: [20, -5] },
    ],
  };
  const circleRadius = 16.5;
  const circleControl = circleRadius * (4 / 3) * Math.tan(Math.PI / 16);
  const circlePoint = (angle) => [
    20 + Math.cos(angle) * circleRadius,
    20 + Math.sin(angle) * circleRadius,
  ];
  const circleTangent = (angle) => [-Math.sin(angle), Math.cos(angle)];
  const circleShape = {
    start: circlePoint(-Math.PI * 0.5),
    segments: Array.from({ length: 8 }, (_, index) => {
      const startAngle = -Math.PI * 0.5 + index * Math.PI * 0.25;
      const endAngle = startAngle + Math.PI * 0.25;
      const start = circlePoint(startAngle);
      const end = circlePoint(endAngle);
      const startTangent = circleTangent(startAngle);
      const endTangent = circleTangent(endAngle);

      return {
        control1: [
          start[0] + startTangent[0] * circleControl,
          start[1] + startTangent[1] * circleControl,
        ],
        control2: [
          end[0] - endTangent[0] * circleControl,
          end[1] - endTangent[1] * circleControl,
        ],
        end,
      };
    }),
  };
  const cueDelayMs = 5000;
  const topThreshold = 4;
  let fireflyReady = false;
  let cueConsumed = (window.scrollY || 0) > topThreshold;
  let departureStarted = false;
  let departureProgress = 0;
  let departureTargetProgress = 0;
  let cueWasPresented = false;
  let hasLeftTop = cueConsumed;
  let activationStartedAt = 0;
  let activationTimer = 0;
  let idleStartedAt = performance.now();
  let morphAmount = 0;
  let morphPopScale = 1;
  let rafId = 0;
  let previousRenderAt = performance.now();

  function showStaticDots() {
    cancelAnimationFrame(rafId);
    rafId = 0;
    fireflyReady = false;
    departureStarted = false;
    cueWasPresented = false;
    dots.style.visibility = "hidden";
    dots.classList.remove("is-idle");
    spacer?.style.removeProperty("opacity");
    spacer?.style.removeProperty("transform");
  }

  function clamp(value, min = 0, max = 1) {
    return Math.min(Math.max(value, min), max);
  }

  function smoothstep(min, max, value) {
    const progress = clamp((value - min) / Math.max(max - min, 0.0001));
    return progress * progress * (3 - 2 * progress);
  }

  function lerp(from, to, amount) {
    return from + (to - from) * amount;
  }

  function backOut(value) {
    const distance = value - 1;
    const tension = 1.65;
    return 1 + (tension + 1) * distance * distance * distance + tension * distance * distance;
  }

  function bouncePulse(time, start, duration) {
    const progress = clamp((time - start) / duration);

    return time >= start && time <= start + duration
      ? Math.sin(progress * Math.PI)
      : 0;
  }

  function morphPath(amount) {
    const interpolatePoint = (teardropPoint, circlePointValue) => [
      lerp(teardropPoint[0], circlePointValue[0], amount),
      lerp(teardropPoint[1], circlePointValue[1], amount),
    ];
    const start = interpolatePoint(teardropShape.start, circleShape.start);
    let path = `M ${start[0].toFixed(2)} ${start[1].toFixed(2)}`;

    teardropShape.segments.forEach((teardropSegment, index) => {
      const circleSegment = circleShape.segments[index];
      const control1 = interpolatePoint(teardropSegment.control1, circleSegment.control1);
      const control2 = interpolatePoint(teardropSegment.control2, circleSegment.control2);
      const end = interpolatePoint(teardropSegment.end, circleSegment.end);

      path += ` C ${control1[0].toFixed(2)} ${control1[1].toFixed(2)}, ${control2[0].toFixed(2)} ${control2[1].toFixed(2)}, ${end[0].toFixed(2)} ${end[1].toFixed(2)}`;
    });

    return `${path} Z`;
  }

  function render(now = performance.now()) {
    rafId = 0;
    if (reducedMotion.matches || document.hidden || (!fireflyReady && !departureStarted)) {
      showStaticDots();
      return;
    }
    const progress = clamp((window.scrollY || 0) / Math.max(window.innerHeight * 0.96, 1));
    const frameDelta = clamp((now - previousRenderAt) / 1000, 0, 1 / 30);
    previousRenderAt = now;

    if (departureStarted) {
      departureTargetProgress = Math.max(departureTargetProgress, progress);
      departureProgress += (departureTargetProgress - departureProgress)
        * (1 - Math.exp(-3.2 * frameDelta));
    }

    const motionProgress = departureStarted ? departureProgress : 0;
    const appearanceProgress = fireflyReady
      ? clamp((now - activationStartedAt) / 460)
      : 0;
    const appearance = fireflyReady
      ? backOut(smoothstep(0, 1, appearanceProgress))
      : 0;
    const appearanceOpacity = smoothstep(0, 0.24, appearanceProgress);
    const isIdle = fireflyReady && !cueConsumed && progress < 0.025;
    const travel = smoothstep(0.02, 0.40, motionProgress);
    const positionTravel = travel;
    const landing = smoothstep(0.30, 0.44, motionProgress);
    const handoff = smoothstep(0.57, 0.635, motionProgress);
    if (handoff >= 1) {
      showStaticDots();
      return;
    }
    const punctuationReveal = handoff;
    const target = anchor.getBoundingClientRect();
    const periodRects = periodMarkers.map((period) => period.getBoundingClientRect());

    dotElements.forEach((dot, index) => {
      const periodRect = periodRects[index];

      if (periodRect) {
        dot.style.width = `${periodRect.width.toFixed(3)}px`;
        dot.style.height = `${target.height.toFixed(3)}px`;
      }
    });

    const landingPulse = Math.sin(landing * Math.PI) * 0.055;
    const scale = lerp(0.58, 1, positionTravel) + landingPulse;
    const fireflyCenter = (fireflyDot?.offsetLeft || 0) + (fireflyDot?.offsetWidth || 12) * 0.5;
    const startX = window.innerWidth * 0.5 - fireflyCenter * scale;
    const startY = window.innerHeight - Math.max(164, window.innerHeight * 0.22);
    const idleSeconds = Math.max((now - idleStartedAt) / 1000, 0);
    const bopCycle = idleSeconds % 6.4;
    const firstBop = bouncePulse(bopCycle, 3.45, 0.48);
    const secondBopPulse = bouncePulse(bopCycle, 4.05, 0.4);
    const secondBop = secondBopPulse * 0.82;
    const bop = isIdle ? Math.max(firstBop, secondBop) : 0;
    const bopVisibility = isIdle ? Math.max(firstBop, secondBopPulse) : 0;
    const floatX = isIdle
      ? Math.sin(idleSeconds * 0.74) * 15 + Math.sin(idleSeconds * 1.31 + 1.2) * 6
      : 0;
    const floatY = isIdle
      ? Math.sin(idleSeconds * 0.86 + 0.55) * 8 + Math.sin(idleSeconds * 1.57) * 3 + bop * 14
      : 0;
    const floatRotation = isIdle
      ? (Math.sin(idleSeconds * 0.63) * 6 + Math.sin(idleSeconds * 1.17 + 0.8) * 2)
        * (1 - bop * 0.9)
      : 0;
    const x = lerp(startX + floatX, periodRects[0]?.left ?? target.left, positionTravel);
    const y = lerp(startY + floatY, target.top, positionTravel);
    const satelliteReveals = [
      smoothstep(0.40, 0.49, motionProgress),
      smoothstep(0.46, 0.57, motionProgress),
    ];
    const floatingOpacity = 1 - handoff;
    const primaryAppearance = appearance;
    const primaryOpacity = appearanceOpacity;
    let morphTarget = morphAmount;

    if (isIdle) {
      morphTarget = 0;
      morphAmount += (morphTarget - morphAmount) * 0.22;
      morphPopScale += (1 - morphPopScale) * 0.2;
    } else if (departureStarted) {
      morphTarget = smoothstep(0.04, 0.30, motionProgress);
      morphAmount += (morphTarget - morphAmount) * 0.18;
      morphPopScale += (1 - morphPopScale) * 0.20;
    } else {
      morphTarget = 0;
      morphAmount += (morphTarget - morphAmount) * 0.22;
    }

    fireflyShape?.setAttribute("d", morphPath(morphAmount));
    fireflyShape?.setAttribute(
      "fill-opacity",
      lerp(
        0.5 + bopVisibility * 0.2,
        1,
        smoothstep(0.08, 0.92, morphAmount),
      ).toFixed(3),
    );
    const punctuationScale = lerp(1, 0.42, positionTravel)
      * morphPopScale
      * lerp(0.16, 1, primaryAppearance);
    const punctuationBaselineShift = lerp(0, 0.34, positionTravel);
    const bopScaleX = 1 - bop * 0.08;
    const bopScaleY = 1 + bop * 0.13;
    fireflySvg?.style.setProperty(
      "transform",
      `translate(-50%, ${punctuationBaselineShift.toFixed(4)}em) rotate(${floatRotation.toFixed(2)}deg) scale(${(punctuationScale * bopScaleX).toFixed(4)}, ${(punctuationScale * bopScaleY).toFixed(4)})`,
    );
    dots.style.visibility = floatingOpacity <= 0.001 ? "hidden" : "visible";
    if (appearanceOpacity > 0.01) cueWasPresented = true;
    spacer?.style.setProperty("opacity", punctuationReveal.toFixed(3));
    spacer?.style.setProperty("transform", "translate3d(0, 0, 0) scale(1)");
    dots.classList.toggle("is-idle", isIdle);
    dots.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;

    dotElements.forEach((dot, index) => {
      if (dot === fireflyDot) {
        dot.style.opacity = (primaryOpacity * floatingOpacity).toFixed(3);
        dot.style.transform = "translate3d(0, 0, 0) scale(1)";
        return;
      }

      const satelliteIndex = index - 1;
      const reveal = satelliteReveals[satelliteIndex];
      const shoot = backOut(reveal);
      const fireflyCenterX = (fireflyDot?.offsetLeft || 0) + (fireflyDot?.offsetWidth || 0) * 0.5;
      const dotCenterX = dot.offsetLeft + dot.offsetWidth * 0.5;
      const overlapX = fireflyCenterX - dotCenterX;
      const translateX = overlapX * (1 - shoot);
      const hopHeight = satelliteIndex === 0 ? 0.20 : 0.26;
      const translateY = -Math.sin(reveal * Math.PI) * hopHeight;
      const satelliteScale = 0.54 + clamp(shoot, 0, 1.08) * 0.46;

      dot.style.opacity = (smoothstep(0, 0.20, reveal) * primaryOpacity * floatingOpacity).toFixed(3);
      dot.style.transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(3)}em, 0) scale(${satelliteScale.toFixed(3)})`;
    });

    if (
      isIdle ||
      (fireflyReady && appearanceProgress < 1) ||
      (departureStarted && Math.abs(departureTargetProgress - departureProgress) > 0.001) ||
      Math.abs(morphTarget - morphAmount) > 0.001 ||
      Math.abs(1 - morphPopScale) > 0.001
    ) {
      requestRender();
    }
  }

  function requestRender() {
    if (!rafId && !document.hidden && !reducedMotion.matches && (fireflyReady || departureStarted)) {
      rafId = requestAnimationFrame(render);
    }
  }

  function syncDotsTypography() {
    dots.style.fontSize = window.getComputedStyle(anchor).fontSize;
  }

  function showFirefly() {
    if (cueConsumed || document.hidden || reducedMotion.matches || (window.scrollY || 0) > topThreshold) {
      return;
    }

    fireflyReady = true;
    activationStartedAt = performance.now();
    idleStartedAt = activationStartedAt;
    window.clearTimeout(activationTimer);
    syncDotsTypography();
    requestRender();
  }

  function scheduleFirefly() {
    window.clearTimeout(activationTimer);

    if (cueConsumed || document.hidden || reducedMotion.matches || (window.scrollY || 0) > topThreshold) {
      return;
    }

    activationTimer = window.setTimeout(showFirefly, cueDelayMs);
  }

  window.addEventListener("scroll", () => {
    const scrollY = window.scrollY || 0;

    if (scrollY > topThreshold) {
      if (!cueConsumed) {
        cueConsumed = true;
        window.clearTimeout(activationTimer);

        if (fireflyReady && cueWasPresented) {
          departureStarted = true;
        } else {
          showStaticDots();
        }
      }

      if (departureStarted) {
        departureTargetProgress = Math.max(
          departureTargetProgress,
          clamp(scrollY / Math.max(window.innerHeight * 0.96, 1)),
        );
      }

      hasLeftTop = true;
    } else if (hasLeftTop) {
      hasLeftTop = false;
      showStaticDots();
      cueConsumed = false;
      departureStarted = false;
      departureProgress = 0;
      departureTargetProgress = 0;
      morphAmount = 0;
      morphPopScale = 1;
      previousRenderAt = performance.now();
      scheduleFirefly();
    }

    requestRender();
  }, { passive: true });
  function syncVisibility() {
    window.clearTimeout(activationTimer);
    showStaticDots();
    // A timer firing in another tab never counts as seeing the teardrop.
    if (!document.hidden && !reducedMotion.matches) scheduleFirefly();
  }
  document.addEventListener("visibilitychange", syncVisibility);
  reducedMotion.addEventListener("change", syncVisibility);
  window.addEventListener("pagehide", () => {
    window.clearTimeout(activationTimer);
    showStaticDots();
  });
  window.addEventListener("pageshow", scheduleFirefly);
  window.addEventListener("resize", () => {
    if (fireflyReady || departureStarted) syncDotsTypography();
    requestRender();
  }, { passive: true });
  document.fonts?.ready.then(() => {
    if (fireflyReady || departureStarted) syncDotsTypography();
    requestRender();
  });

  showStaticDots();
  scheduleFirefly();
  requestRender();
}
